import { DEFAULT_FIELD_REQUESTS } from "utils/fieldRequests/defaultFieldRequests";
import * as yup from "yup";

import type { FileMetadata } from "@krea/shared/hooks/useFileUpload";
import type { Currency } from "@krea/shared/types/common";

import { BidTypeEnum, FieldRequestFieldEnum } from "@/features/common/enums";
import type { ILoanApplicationExtended } from "@/features/lender-applications/types/loan-application";
import type {
  IFieldRequestInput,
  IPromotionInput,
  UnionBidInput,
} from "@/features/lender-responses/types/lender-response";

import { bidFieldRequestSchema } from "./bidFieldRequest";
import { bidPromotionSchema } from "./bidPromotion";
import {
  companyLoanBidDefaultValues,
  companyLoanBidSchema,
} from "./companyLoanBid";
import {
  factoringBidValidationSchema,
  invoicePurchasedBidDefaultValues,
} from "./invoicePurchasedBid";
import { leasingBidDefaultValues, leasingBidSchema } from "./leasingBid";
import {
  realEstateBidDefaultValues,
  realEstateBidSchema,
} from "./realEstateBid";
import {
  revolvingCreditBidDefaultValues,
  revolvingCreditBidSchema,
} from "./revolvingCreditBid";
import {
  purchaseFinancingBidDefaultValues,
  purchaseFinancingBidSchema,
} from "./purchaseFinancingBid";

interface BidValidationProps {
  currency: Currency;
  formatWithCurrency: (value: number) => string;
  estimatedInvoiceAmount: number;
}

export interface BidPostData<T extends UnionBidInput = UnionBidInput> {
  // bid?: ICompanyLoanBidInput | null;
  // invoice_purchase_bid?: IFactoringBidInput | null;
  // real_estate_bid?: IRealEstateBidInput | null;
  // leasing_bid?: ILeasingBidInput | null;
  // revolving_credit_bid?: IRevolvingCreditBidInput | null;
  bids: T[];
  fieldRequests: IFieldRequestInput[];
  promotions?: IPromotionInput[] | null;
  creditTermsFile?: File | FileMetadata | null;
}

// Reusable helper: a schema that enforces a literal value for `type`
const literalType = <T extends string>(v: T) =>
  yup.object({ type: yup.mixed<T>().oneOf([v]).required() });

// Factory for the discriminated bid schema
const makePolymorphicBidSchema = (
  currency: Currency,
  formatWithCurrency: (n: number) => string,
  estimatedInvoiceAmount?: number,
) => {
  // IMPORTANT: ensure each concrete schema also asserts its own literal `type`
  const byType: Record<BidTypeEnum, yup.ObjectSchema<any>> = {
    [BidTypeEnum.COMPANY_LOAN]: companyLoanBidSchema(
      currency,
      formatWithCurrency,
    ).concat(literalType(BidTypeEnum.COMPANY_LOAN)),

    [BidTypeEnum.FACTORING]: factoringBidValidationSchema(
      estimatedInvoiceAmount ?? 0,
      currency,
      formatWithCurrency,
    ).concat(literalType(BidTypeEnum.FACTORING)),

    [BidTypeEnum.REAL_ESTATE]: realEstateBidSchema(
      currency,
      formatWithCurrency,
    ).concat(literalType(BidTypeEnum.REAL_ESTATE)),

    [BidTypeEnum.LEASING]: leasingBidSchema(
      currency,
      formatWithCurrency,
    ).concat(literalType(BidTypeEnum.LEASING)),

    [BidTypeEnum.REVOLVING_CREDIT]: revolvingCreditBidSchema(
      currency,
      formatWithCurrency,
    ).concat(literalType(BidTypeEnum.REVOLVING_CREDIT)),

    [BidTypeEnum.PURCHASE_FINANCING]: purchaseFinancingBidSchema(
      currency,
      formatWithCurrency,
    ).concat(literalType(BidTypeEnum.PURCHASE_FINANCING)),
  };

  // Lazy selector that routes to the correct concrete schema by discriminator
  const polymorphicBidSchema = yup.lazy((val: UnionBidInput) => {
    const t = val.type;

    return byType[t];
  });

  return polymorphicBidSchema;
};

export const bidValidationSchema: (
  props: BidValidationProps,
) => yup.ObjectSchema<BidPostData> = ({
  currency,
  formatWithCurrency,
  estimatedInvoiceAmount,
}) => {
  const polymorphicBidSchema = makePolymorphicBidSchema(
    currency,
    formatWithCurrency,
    estimatedInvoiceAmount,
  );

  return yup.object().shape({
    // polymorphic list
    bids: yup
      .array()
      .of(polymorphicBidSchema) // <- discriminator-driven validation per element
      .required()
      .min(1, "At least one bid is required")
      .default([]),
    // bid: companyLoanBidSchema(currency, formatWithCurrency).default(undefined),
    // invoice_purchase_bid: factoringBidValidationSchema(
    //   estimatedInvoiceAmount,
    //   currency,
    //   formatWithCurrency,
    // ).default(undefined),
    // real_estate_bid: realEstateBidSchema(currency, formatWithCurrency).default(
    //   undefined,
    // ),
    // leasing_bid: leasingBidSchema(currency, formatWithCurrency)
    //   .notRequired()
    //   .default(undefined),
    // revolving_credit_bid: revolvingCreditBidSchema(
    //   currency,
    //   formatWithCurrency,
    // ).default(undefined),
    fieldRequests: yup.array().of(bidFieldRequestSchema).required().default([]),
    promotions: yup
      .array()
      .of(bidPromotionSchema)
      .notRequired()
      .default(undefined),
    creditTermsFile: yup
      .mixed<File | FileMetadata>()
      .notRequired()
      .default(undefined),
  });
};

export const getLenderResponseDefaultValues = (
  primaryBidType: BidTypeEnum,
  loanApplication: ILoanApplicationExtended,
): BidPostData => {
  const { applyingCompany } = loanApplication;
  const { estimatedInvoiceAmount, averagePaymentTimeDays } = applyingCompany;
  const guarantorFr = DEFAULT_FIELD_REQUESTS[FieldRequestFieldEnum.guarantors];
  let initialBid: UnionBidInput;

  switch (primaryBidType) {
    case BidTypeEnum.COMPANY_LOAN:
      initialBid = {
        ...companyLoanBidDefaultValues,
        type: primaryBidType,
      };
      break;
    case BidTypeEnum.FACTORING:
      initialBid = {
        ...invoicePurchasedBidDefaultValues,
        type: primaryBidType,
        purchaseLimit: estimatedInvoiceAmount ?? undefined,
        paymentTermsDays: averagePaymentTimeDays ?? undefined,
      };
      break;
    case BidTypeEnum.REAL_ESTATE:
      initialBid = {
        ...realEstateBidDefaultValues,
        type: primaryBidType,
      };
      break;
    case BidTypeEnum.LEASING:
      initialBid = {
        ...leasingBidDefaultValues,
        type: primaryBidType,
      };
      break;
    case BidTypeEnum.REVOLVING_CREDIT:
      initialBid = {
        ...revolvingCreditBidDefaultValues,
        type: primaryBidType,
      };
      break;
    case BidTypeEnum.PURCHASE_FINANCING:
      initialBid = {
        ...purchaseFinancingBidDefaultValues,
        type: primaryBidType,
      };
      break;

    default: {
      const exhaustiveCheck: never = primaryBidType;
      console.error(exhaustiveCheck);
      throw new Error(`Exhaustive check failed ${primaryBidType}`);
    }
  }
  const bids: UnionBidInput[] = [initialBid];

  const initialValues: BidPostData = {
    bids,
    fieldRequests: guarantorFr ? [guarantorFr] : [],
    promotions: [],
    creditTermsFile: undefined,
  };

  return initialValues;
};
